import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import type React from 'react'

const FieldContext = createContext<{ mandatory?: boolean }>({})

export const Fieldset = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldsetProps, 'as' | 'className'>) => {
  return (
    <Headless.Fieldset
      {...props}
      className={clsx(className, '*:data-[slot=text]:mt-1 [&>*+[data-slot=control]]:mt-6')}
    />
  )
}

export const Legend = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.LegendProps, 'as' | 'className'>) => {
  return (
    <Headless.Legend
      data-slot="legend"
      {...props}
      className={clsx(
        className,
        'text-base/6 font-semibold text-gray-900 data-disabled:opacity-50 sm:text-sm/6 dark:text-white',
      )}
    />
  )
}

export const FieldGroup = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      data-slot="control"
      {...props}
      className={clsx(className, 'space-y-8')}
    />
  )
}

export const Field = ({
  className,
  mandatory,
  ...props
}: { className?: string; mandatory?: boolean } & Omit<Headless.FieldProps, 'as' | 'className'>) => {
  return (
    <FieldContext.Provider value={{ mandatory }}>
      <Headless.Field
        {...props}
        className={clsx(
          className,
          '[&>[data-slot=label]+[data-slot=control]]:mt-3',
          '[&>[data-slot=label]+[data-slot=description]]:mt-1',
          '[&>[data-slot=description]+[data-slot=control]]:mt-3',
          '[&>[data-slot=control]+[data-slot=description]]:mt-3',
          '[&>[data-slot=control]+[data-slot=error]]:mt-3',
          '*:data-[slot=label]:font-medium',
        )}
      />
    </FieldContext.Provider>
  )
}

export const Label = ({
  className,
  mandatory,
  children,
  ...props
}: { className?: string; mandatory?: boolean; children?: ReactNode } & Omit<
  Headless.LabelProps,
  'as' | 'className' | 'children'
>) => {
  const fieldContext = useContext(FieldContext)
  const showMandatory = mandatory !== undefined ? mandatory : (fieldContext.mandatory ?? false)

  return (
    <Headless.Label
      data-slot="label"
      {...props}
      className={clsx(
        className,
        'text-base/6 text-gray-900 select-none data-disabled:opacity-50 sm:text-sm/6 dark:text-white',
      )}
    >
      {children}
      {showMandatory && (
        <>
          {' '}
          <span
            className="text-red-500"
            aria-hidden="true"
          >
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      )}
    </Headless.Label>
  )
}

export const Description = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'as' | 'className'>) => {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(className, 'text-base/6 text-gray-500 data-disabled:opacity-50 sm:text-sm/6 dark:text-gray-400')}
    />
  )
}

export const ErrorMessage = ({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'as' | 'className'>) => {
  return (
    <Headless.Description
      data-slot="error"
      {...props}
      className={clsx(className, 'text-base/6 text-red-600 data-disabled:opacity-50 sm:text-sm/6 dark:text-red-500')}
    />
  )
}
